import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import { getCustomerSettings } from "@/api/settings";
import client from "@/client";

import { PrimaryButton } from "@/pages/shared/shared.styles";

import DateFilter from "@/components/filters/date";
import { MultiSelectDropdown } from "@/components/filters/multi-select/multi-select";
import type { MultiSelectOption } from "@/components/filters/multi-select/multi-select.types";
import TraineesFilter from "@/components/filters/trainees";
import { ChevronDownSquareCornersIcon24, DownloadIcon24, FileIcon24 } from "@/components/icons";
import { VRIntlProviderComponent } from "@/components/providers/intl-provider";
import Loader from "@/components/ui/loader";
import Page from "@/components/ui/page";

import base from "@/themes/base/base";

import { DropdownFilterVariant } from "@/types/filters";

import { FiltersList } from "@/enums/dashboard";

import {
    Container,
    Text,
    Title,
    FileTypeToggle,
    ToggleWrapper,
    Wrapper,
    LoaderContainer,
} from "./reports.styles";

const localeFn = (target: string) => import(`./locale/${target.toLowerCase()}.json`);

function getFilenameFromContentDisposition(headerValue?: string) {
    if (!headerValue) return null;

    // examples:
    // attachment; filename="SessionList_23012026_16.05.xlsx"
    // attachment; filename=SessionList_23012026_16.05.xlsx
    const match = /filename\*?=(?:UTF-8''|")?([^;"\n"]+)/i.exec(headerValue);

    if (!match?.[1]) return null;

    try {
        return decodeURIComponent(match[1].replace(/"/g, "").trim());
    } catch {
        return match[1].replace(/"/g, "").trim();
    }
}

function downloadBlob(data: BlobPart, filename: string, mimeType?: string) {
    const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";

    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
}

const Reports = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams = new URLSearchParams(window.location.search);
    const dateRange = queryParams.get("dateRange");
    const currentPath = `${location.pathname}?dateRange=last7days`;

    const [openFilter, setOpenFilter] = useState<FiltersList>(FiltersList.None);
    const [disabled, setDisabled] = useState(false);

    const [reportOptions, setReportOptions] = useState<
        Array<{ key: string; name?: string; configurationKey?: string }>
    >([]);
    const [defaultReportKey, setDefaultReportKey] = useState<string>("");
    const [selectedReportKey, setSelectedReportKey] = useState<string>("");

    const [filters, setFilters] = useState<{
        userId: string | null;
        from: Date;
        to: Date;
    }>({
        userId: null,
        from: new Date(),
        to: new Date(),
    });

    useEffect(() => {
        navigate(currentPath);
    }, []);

    useEffect(() => {
        let cancelled = false;

        getCustomerSettings()
            .then((settings: any) => {
                if (cancelled) return;

                const reports = Array.isArray(settings?.reports) ? settings.reports : [];
                const defaultKey =
                    typeof settings?.defaultReport === "string" ? settings.defaultReport : "";

                setReportOptions(reports);
                setDefaultReportKey(defaultKey);

                // If the UI hasn't picked anything yet, seed it with the default (or first available).
                setSelectedReportKey((prev) => {
                    if (prev) return prev;

                    if (defaultKey) return defaultKey;

                    return reports?.[0]?.key ?? "";
                });
            })
            .catch(() => {
                // Ignore, we can still download the backend default report.
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const handleOnClick = (filterType: FiltersList) => {
        setOpenFilter((prevFilter) => {
            return prevFilter === filterType ? FiltersList.None : filterType;
        });
    };

    const handleDateChange = (from: Date, to: Date) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            from,
            to,
        }));
    };

    const handleTraineeChange = (user: string | null) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            userId: user,
        }));
    };

    const handleDownload = async () => {
        setDisabled(true);

        try {
            const reportKeyToUse =
                selectedReportKey || defaultReportKey || reportOptions?.[0]?.key || undefined;

            const response = await client.get("/v1/Report/download", {
                params: {
                    from: filters.from?.toISOString(),
                    to: filters.to?.toISOString(),
                    userId: filters.userId ?? undefined,
                    option: reportKeyToUse,
                    // endpoint defaults to xlsx.. keep implicit until we design the file type selector
                },
                responseType: "blob",
            });

            const contentType =
                (response.headers && (response.headers["content-type"] as string)) ||
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

            const filename =
                getFilenameFromContentDisposition(
                    response.headers && (response.headers["content-disposition"] as string),
                ) || "SessionList.xlsx";

            downloadBlob(response.data, filename, contentType);

            handleTraineeChange(null);
        } catch (err) {
            // axios interceptor will handle 401s
            // eslint-disable-next-line no-console
            console.error(err);
        } finally {
            setDisabled(false);
        }
    };

    const reportSelectOptions: MultiSelectOption[] = useMemo(() => {
        return (reportOptions || [])
            .filter((r) => typeof r?.key === "string" && r.key.trim())
            .map((r) => ({
                id: r.key,
                label: typeof r?.name === "string" && r.name.trim() ? r.name : r.key,
            }));
    }, [reportOptions]);

    const initialSelectedReportOptions: MultiSelectOption[] = useMemo(() => {
        const k = selectedReportKey || defaultReportKey;

        if (!k) return [];
        const opt = reportSelectOptions.find((o) => o.id === k);

        return opt ? [opt] : [];
    }, [selectedReportKey, defaultReportKey, reportSelectOptions]);

    return (
        <VRIntlProviderComponent localeFn={localeFn} id="reports-page" fallback={null}>
            <Page title="Reports">
                <Wrapper>
                    <Container>
                        <Title>
                            <FormattedMessage id={"reports:container-title"} />
                        </Title>

                        <ToggleWrapper>
                            {reportSelectOptions.length > 0 ? (
                                <MultiSelectDropdown
                                    handleOnClick={() => {
                                        /* local open/close managed inside component */
                                    }}
                                    onSelectionChange={(selected) => {
                                        // Treat as single-select: take first selected option.
                                        const first = selected?.[0]?.id ?? "";
                                        setSelectedReportKey(first);
                                    }}
                                    options={reportSelectOptions}
                                    initialSelectedOptions={initialSelectedReportOptions}
                                    fullWidth
                                    singleSelect
                                    title={
                                        initialSelectedReportOptions?.[0]?.label ||
                                        // Keep the existing UX label when nothing is selected yet.
                                        "Sessions Report - XLSX file"
                                    }
                                    icon={<FileIcon24 />}
                                    showSelectAll={false}
                                    variant={DropdownFilterVariant.PrimaryBlack900}
                                    popUpWidth={"16.125rem"}
                                />
                            ) : (
                                <FileTypeToggle fullWidth disabled>
                                    <FileIcon24 />
                                    <Text>
                                        <FormattedMessage id={"reports:filetype:xslx"} />
                                    </Text>
                                    <ChevronDownSquareCornersIcon24 />
                                </FileTypeToggle>
                            )}

                            <TraineesFilter
                                isOpen={openFilter === FiltersList.TraineesList}
                                backgroundColor={base.colors.neutral.grey[900]}
                                handleOnClick={() => handleOnClick(FiltersList.TraineesList)}
                                onUserSelected={handleTraineeChange}
                                initialUserId={filters.userId}
                                fullWidth
                                variant={DropdownFilterVariant.PrimaryBlack900}
                                asInputField
                            />

                            <DateFilter
                                isOpen={openFilter === FiltersList.DatePicker}
                                handleOnClick={() => handleOnClick(FiltersList.DatePicker)}
                                onChange={handleDateChange}
                                initialDateRange={{ from: filters.from, to: filters.to }}
                                dateRange={dateRange}
                                fullWidth
                                variant={DropdownFilterVariant.PrimaryBlack900}
                                asInputField
                            />
                        </ToggleWrapper>

                        <PrimaryButton
                            fullWidth
                            onClick={(_e: React.MouseEvent) => {
                                handleDownload();
                            }}
                            disabled={disabled}
                        >
                            {disabled ? (
                                <LoaderContainer>
                                    <Loader size="medium" color="#666666" fontSize="1.5rem" />
                                </LoaderContainer>
                            ) : (
                                <>
                                    <DownloadIcon24 fill="#121212" />
                                    <FormattedMessage id={"reports:download"} />
                                </>
                            )}
                        </PrimaryButton>
                    </Container>
                </Wrapper>
            </Page>
        </VRIntlProviderComponent>
    );
};

export default Reports;
